import { PostingBody } from './PostingPreview';
import { payLabel } from './postingFormat';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import AppNav from './AppNav';
import { translations as mosTranslations } from './data/mosTranslations';
import {
  buildUsaJobsSearchRequest,
  searchUsaJobs,
  fetchJobByMatchedObjectId,
  mapJob,
  timeAgo,
} from './usajobs';
import './Card.css';
import './JobsPage.css';

const CLEARANCE_LEVELS = ['All Clearances', 'TS/SCI', 'Secret', 'Public Trust'];
const APP_STATUS_LABEL = { applied: 'Applied', reviewing: 'Reviewing', interview: 'Interview', offer: 'Offer', rejected: 'Not Selected', withdrawn: 'Withdrawn' };
const WITHDRAWABLE_STATUSES = ['applied', 'reviewing', 'interview'];
const REMOTE_OPTIONS   = ['All Locations', 'Remote', 'Hybrid', 'On-site'];
const SORT_OPTIONS     = ['Newest', 'Salary: High to Low'];
const RADIUS_OPTIONS   = [
  { value: '10',         label: '10 miles' },
  { value: '25',         label: '25 miles' },
  { value: '50',         label: '50 miles' },
  { value: '75',         label: '75 miles' },
  { value: '100',        label: '100 miles' },
  { value: 'Nationwide', label: 'Nationwide' },
];

// Patch 1.3h — Cyber/IT/security career goals route to USAJobs occupational
// series 2210 (Information Technology Management) instead of keyword search.
// Keyword search alone returns too few results (e.g. "Cybersecurity" -> 4).
// 2210 returns the full federal IT opportunity set, which is the right
// initial view for a transitioning cyber/IT veteran.
const CYBER_IT_PATTERN = /\b(cyber|cybersecurity|security|infosec|information security|cloud security|it|information technology|network|systems? admin|soc|penetration|pen tester|devsecops)\b/i;

function isCyberITGoal(goal) {
  if (!goal || typeof goal !== 'string') return false;
  return CYBER_IT_PATTERN.test(goal);
}

// Patch 1.3g — Broaden the initial profile-driven job query so a narrow
// career_goal like "Cybersecurity Analyst" doesn't return only 1 result.
// Strips common role suffixes (Analyst, Engineer, Manager, etc.) plus
// optional seniority modifiers (II, III, Senior) from the END of the goal.
// Used ONLY for the initial fetchJobs call. User-typed searches are
// passed through unchanged.
function normalizeInitialJobQuery(careerGoal) {
  if (!careerGoal || typeof careerGoal !== 'string') return 'cybersecurity';
  const cleaned = careerGoal.trim();
  if (!cleaned) return 'cybersecurity';

  const ROLE_SUFFIXES = [
    'Analyst', 'Engineer', 'Specialist', 'Manager', 'Coordinator',
    'Director', 'Administrator', 'Architect', 'Consultant', 'Lead',
    'Officer', 'Technician', 'Developer', 'Designer'
  ];

  const suffixPattern = new RegExp(
    `\\s+(${ROLE_SUFFIXES.join('|')})(\\s+(I{1,3}|IV|V|Senior|Junior|Lead))?\\s*$`,
    'i'
  );

  const broadened = cleaned.replace(suffixPattern, '').trim();
  return broadened || cleaned;
}

// Phase 5 — Matching polish. Every profile carries a real occupation_code
// (MOS/AFSC/rating) from onboarding, independent of the separate, optional
// career_goal dropdown. Previously, a veteran with no career_goal set fell
// back to a hardcoded 'cybersecurity' search regardless of their actual
// background -- an Army logistics NCO or a Navy corpsman with no career
// goal chosen would see cyber roles by default. This derives a real
// civilian-title suggestion from the occupation code's translation entry
// (src/data/mosTranslations.js, the same dataset CivilianTranslation.jsx
// uses) instead of guessing.
function deriveCareerGoalFromOccupation(occupationCode) {
  if (!occupationCode) return null;
  const entry = mosTranslations[occupationCode];
  return entry?.civilianTitles?.[0] || null;
}

// mapJob (USAJobs result → card shape) now lives in src/usajobs.js alongside
// the proxy client, with explicit source attribution and the honest
// 'Not specified' clearance fallback.

// Phase 2: map a job_postings row (VetQualified-native posting) into the
// same shape mapJob() produces from USAJobs, so both render in one list.
// Platform postings aren't saveable via savedJobs today -- that column
// stores raw USAJobs MatchedObjectId and D-saved-jobs' refetch/tombstone
// logic assumes it can query USAJobs by that id, which a platform posting's
// uuid isn't. Extending saving to platform jobs is follow-up work, not this
// pass -- the save button is simply omitted for source: 'platform' cards.
function mapPlatformJob(row) {
  const company = row.employer_profiles?.company_name || 'VetQualified Employer';
  const clearanceCode = row.clearance_required === 'TS/SCI' ? 'tssci' : row.clearance_required === 'Secret' ? 'secret' : row.clearance_required === 'Public Trust' ? 'public' : row.clearance_required === 'Top Secret' ? 'topsecret' : row.clearance_required === 'No clearance required' ? 'none' : 'unspecified';
  return {
    id: `vq-${row.id}`,
    postingId: row.id,
    source: 'platform',
    employerId: row.employer_id,
    title: row.title,
    company,
    logo: company.slice(0, 2).toUpperCase(),
    logoColor: '#C9A84C',
    location: row.location || 'Location not listed',
    remote: row.remote_type,
    clearance: row.clearance_required,
    clearanceCode,
    salary: payLabel(row),
    type: row.employment_type,
    sponsorship: false,
    posted: timeAgo(row.created_at),
    description: row.description,
    summary: row.summary,
    posting: row,
    tags: [],
    matchReasons: [],
  };
}

// Phase E.1: Build a clean "City, ST" label from a Nominatim result.
// Nominatim returns address components like { city, town, village, county, state, "ISO3166-2-lvl4" }.
// We prefer city > town > village > county for the locality, and use the
// 2-letter state code from ISO3166-2-lvl4 (e.g. "US-NV" -> "NV"). Returns
// null if the result is not a U.S. place we can label cleanly.
const STATE_CODES = new Set(['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC']);

function normalizeNominatimResult(r) {
  if (!r || !r.address) return null;
  if (r.address.country_code !== 'us') return null;
  const a = r.address;
  const locality = a.city || a.town || a.village || a.hamlet || a.county || null;
  const iso = a['ISO3166-2-lvl4'] || '';
  const stateCode = iso.startsWith('US-') ? iso.slice(3) : '';
  if (!locality || !STATE_CODES.has(stateCode)) return null;
  return {
    label: `${locality}, ${stateCode}`,
    placeId: r.place_id,
  };
}

export default function JobsPage() {
  const [jobs,         setJobs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [totalJobs,    setTotalJobs]    = useState(0);
  const [search,       setSearch]       = useState('');
  const [searchInput,  setSearchInput]  = useState('');
  const [clearFilter,  setClearFilter]  = useState('All Clearances');
  const [remoteFilter, setRemoteFilter] = useState('All Locations');
  const [sortBy,       setSortBy]       = useState('Newest');
  const [savedJobs,    setSavedJobs]    = useState([]);
  const [expandedJob,  setExpandedJob]  = useState(null);
  const [applyJob,     setApplyJob]     = useState(null);
  const [userId,       setUserId]       = useState(null);
  const [jobMatches,   setJobMatches]   = useState(0);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const filterTriggerRef = useRef(null);
  const closeFilters = () => {
    setFilterDrawerOpen(false);
    if (window.matchMedia('(max-width: 900px)').matches) filterTriggerRef.current?.focus();
  };
  const [showSavedOnly,    setShowSavedOnly]    = useState(false);
  const [locationInput,    setLocationInput]    = useState('');
  const [radius,           setRadius]           = useState('Nationwide');
  // Phase E.1: location autocomplete state
  const [locSuggestions,   setLocSuggestions]   = useState([]);
  const [locDropdownOpen,  setLocDropdownOpen]  = useState(false);
  const [locLoading,       setLocLoading]       = useState(false);
  const [locHighlight,     setLocHighlight]     = useState(-1);
  const locDebounceRef = useRef(null);
  const locWrapRef     = useRef(null);
  const drawerLocWrapRef = useRef(null);
  // D-saved-jobs Phase 2: cache of saved jobs that aren't in the current `jobs`
  // array — keyed by MatchedObjectId. Populated on demand when Saved Only
  // toggle activates. Persists across toggle on/off to avoid re-fetching.
  const [savedJobsCache,   setSavedJobsCache]   = useState({});
  const [loadingSavedJobs, setLoadingSavedJobs] = useState(false);
  // Phase 2: VetQualified-native postings, fetched once and filtered
  // client-side alongside the USAJobs results below.
  const [platformJobs,     setPlatformJobs]     = useState([]);
  const [messagingStatus,  setMessagingStatus]  = useState('idle'); // idle | sending | error
  // Phase 3: in-platform applications for VetQualified-native postings.
  const [resumeUrl,          setResumeUrl]          = useState('');
  const [resumeName,         setResumeName]         = useState('');
  const [myApplications,     setMyApplications]     = useState([]);
  const [showApplicationsOnly, setShowApplicationsOnly] = useState(false);
  const [applyDraft,         setApplyDraft]         = useState('');
  const [applyStatus,        setApplyStatus]        = useState('idle'); // idle | submitting | error
  const applyPending = useRef(false);
  const withdrawalPending = useRef(new Set());
  const [withdrawals, setWithdrawals] = useState({});
  const navigate = useNavigate();

  const applicationsByPostingId = {};
  myApplications.forEach(a => { applicationsByPostingId[a.job_posting_id] = a; });

  useEffect(() => {
    supabase
      .from('job_postings')
      .select('id, employer_id, title, description, location, remote_type, clearance_required, salary_min, salary_max, employment_type, summary, details, is_test, created_at, employer_profiles:employer_profiles_public(company_name)')
      .eq('status', 'active')
      .eq('is_test', false)
      .order('created_at', { ascending: false })
      .then(({ data }) => setPlatformJobs((data || []).map(mapPlatformJob)));
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUserId(session.user.id);
        const { data } = await supabase
          .from('profiles')
          .select('career_goal, location, job_matches, saved_jobs, resume_url, resume_name, occupation_code, occupation_branch')
          .eq('id', session.user.id)
          .single();

        // Phase 5: prefer the explicit career_goal the veteran chose. If
        // they haven't set one, derive a real suggestion from their actual
        // occupation code rather than guessing 'cybersecurity' for
        // everyone. If neither exists, search stays career-neutral --
        // same as the anonymous-visitor case below -- rather than forcing
        // an unrelated keyword.
        let goal = data?.career_goal || null;
        let goalSource = goal ? 'goal' : null;
        if (!goal && data?.occupation_code) {
          const derived = deriveCareerGoalFromOccupation(data.occupation_code);
          if (derived) { goal = derived; goalSource = 'occupation'; }
        }
        const loc  = data?.location    || '';
        setJobMatches(data?.job_matches || 0);
        setResumeUrl(data?.resume_url || '');
        setResumeName(data?.resume_name || '');

        // Phase 3: hydrate this veteran's applications so already-applied
        // platform postings show status instead of an Apply button, and so
        // the "My Applications" view has data regardless of whether those
        // postings are still in the current jobs/platformJobs fetch.
        const { data: appsData } = await supabase
          .from('applications')
          .select('id, job_posting_id, status, cover_note, created_at, job_postings(title, employer_profiles:employer_profiles_public(company_name))')
          .eq('applicant_id', session.user.id)
          .order('created_at', { ascending: false });
        setMyApplications(appsData || []);
        // D-saved-jobs: hydrate saved jobs from Supabase on mount so they
        // persist across refresh and sessions. Defaults to [] if column is
        // null (shouldn't happen given NOT NULL DEFAULT, but defensive).
        setSavedJobs(Array.isArray(data?.saved_jobs) ? data.saved_jobs : []);
        // Phase E.1: do NOT prefill location from profile. A transitioning
        // veteran may want to search where they currently live OR where they
        // plan to relocate to. An empty location input invites that choice
        // instead of anchoring them to their current address. The profile
        // location is still used for the initial fetch keyword path below
        // (non-cyber goals) so first results are still relevant.
        // Patch 1.3f: do not prefill search input or search state from career goal.
        // The goal is used only as the initial fetchJobs keyword so results are
        // personalized on first load, while the search bar remains empty.
        // Patch 1.3g: broaden narrow career goals (e.g. "Cybersecurity Analyst"
        // -> "Cybersecurity") so the initial fetch returns more useful results.
        // Patch 1.3h: cyber/IT/security goals route to occupational series 2210
        // for a healthy nationwide opportunity set. Cyber/IT initial fetch is
        // intentionally nationwide (Phase E rule) — location only narrows on
        // user-driven search.
        const matchReason = goalSource === 'occupation'
          ? `Matches your ${data?.occupation_branch || 'military'} occupation (${data.occupation_code} → ${goal})`
          : goalSource === 'goal'
          ? `Matches your career goal: ${goal}`
          : null;

        if (goal && isCyberITGoal(goal)) {
          fetchJobs(null, '', 'All Locations', 'Newest', '2210', 'Nationwide', matchReason);
        } else if (goal) {
          const initialKeyword = normalizeInitialJobQuery(goal);
          fetchJobs(initialKeyword, loc, 'All Locations', 'Newest', undefined, 'Nationwide', matchReason);
        } else {
          // No career_goal and no occupation_code on file -- same
          // career-neutral fetch as an anonymous visitor, not a guess.
          fetchJobs('', loc, 'All Locations', 'Newest', undefined, 'Nationwide');
        }
      } else {
        // Phase E: no session = career-neutral broad federal listings. Do NOT
        // default anonymous users into a cybersecurity keyword.
        fetchJobs('', '', 'All Locations', 'Newest', undefined, 'Nationwide');
      }
    });
  }, []);

  // D-saved-jobs Phase 2: when Saved Only is toggled on, fetch any saved jobs
  // that aren't in the current `jobs` array AND aren't already cached. Bounded:
  // does nothing when showSavedOnly is false, when savedJobs is empty, or when
  // every saved ID is already covered. Each missing ID fires one parallel
  // fetch via Keyword search; nulls (job no longer available) are silently
  // skipped for MVP.
  useEffect(() => {
    if (!showSavedOnly || savedJobs.length === 0) return;
    const missingIds = savedJobs.filter(
      id => !jobs.some(j => j.id === id) && !savedJobsCache[id]
    );
    if (missingIds.length === 0) return;
    setLoadingSavedJobs(true);
    Promise.all(missingIds.map(fetchJobByMatchedObjectId)).then(results => {
      // D-saved-jobs Phase 2: cache every result, including unavailable
      // tombstones, so the UI shows "no longer available" instead of an
      // empty list and so we don't refetch known-bad IDs.
      const additions = {};
      results.forEach((entry, i) => {
        if (entry) additions[missingIds[i]] = entry;
      });
      if (Object.keys(additions).length > 0) {
        setSavedJobsCache(prev => ({ ...prev, ...additions }));
      }
      setLoadingSavedJobs(false);
    });
  }, [showSavedOnly, savedJobs, jobs, savedJobsCache]);

  // Phase E.1: debounced Nominatim autocomplete for the location input.
  // Triggers when locationInput changes; ignores updates that came from
  // selecting a suggestion (we close the dropdown then). Aborts on unmount.
  useEffect(() => {
    if (locDebounceRef.current) clearTimeout(locDebounceRef.current);
    const q = locationInput.trim();
    if (q.length < 2) {
      setLocSuggestions([]);
      setLocLoading(false);
      return;
    }
    if (!locDropdownOpen) return; // user just selected; don't re-query
    setLocLoading(true);
    locDebounceRef.current = setTimeout(async () => {
      try {
        // countrycodes=us limits to U.S. results.
        // addressdetails=1 returns the structured address we need to label cleanly.
        // featuretype=settlement biases toward cities/towns (still server-side filtered).
        const url = `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(q)}` +
          `&format=jsonv2&addressdetails=1&countrycodes=us&limit=8`;
        const res = await fetch(url, {
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) {
          setLocSuggestions([]);
          setLocLoading(false);
          return;
        }
        const data = await res.json();
        // Normalize, drop nulls, dedupe by label, cap at 6.
        const seen = new Set();
        const out = [];
        for (const r of data) {
          const norm = normalizeNominatimResult(r);
          if (!norm) continue;
          if (seen.has(norm.label)) continue;
          seen.add(norm.label);
          out.push(norm);
          if (out.length >= 6) break;
        }
        setLocSuggestions(out);
        setLocHighlight(-1);
        setLocLoading(false);
      } catch {
        setLocSuggestions([]);
        setLocLoading(false);
      }
    }, 300);
    return () => {
      if (locDebounceRef.current) clearTimeout(locDebounceRef.current);
    };
  }, [locationInput, locDropdownOpen]);

  // Phase E.1: close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (locWrapRef.current && locWrapRef.current.contains(e.target)) return;
      if (drawerLocWrapRef.current && drawerLocWrapRef.current.contains(e.target)) return;
      setLocDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLocChange = (val) => {
    setLocationInput(val);
    setLocDropdownOpen(true);
    if (val.trim().length < 2) {
      setLocSuggestions([]);
    }
  };

  const handleLocSelect = (suggestion) => {
    setLocationInput(suggestion.label);
    setLocSuggestions([]);
    setLocDropdownOpen(false);
    setLocHighlight(-1);
  };

  const handleLocKeyDown = (e) => {
    if (!locDropdownOpen || locSuggestions.length === 0) {
      if (e.key === 'Enter') handleSearch();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setLocHighlight(h => Math.min(h + 1, locSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setLocHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (locHighlight >= 0 && locHighlight < locSuggestions.length) {
        e.preventDefault();
        handleLocSelect(locSuggestions[locHighlight]);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setLocDropdownOpen(false);
    }
  };

  const fetchJobs = async (kw, loc, remote, sort, categoryCode, radiusVal, matchReason = null) => {
    setLoading(true);
    setError(null);
    // Phase E rules (Remote omits location/radius; Nationwide omits both)
    // live in buildUsaJobsSearchRequest, and the request runs through the
    // usajobs-search Edge Function so no USAJOBS credential ever reaches
    // the browser. The proxy surfaces upstream auth/rate-limit failures as
    // real errors, so an outage never looks like "no jobs match your search."
    const { data, error: searchError } = await searchUsaJobs(buildUsaJobsSearchRequest({
      keyword: kw,
      locationName: loc,
      radius: radiusVal,
      remoteOnly: remote === 'Remote',
      categoryCode,
      sortBySalary: sort === 'Salary: High to Low',
    }));
    if (searchError) {
      // FunctionsFetchError = the request never reached the proxy (their
      // connection); anything else = the proxy or USAJobs itself failed.
      setError(searchError.name === 'FunctionsFetchError'
        ? 'Failed to load jobs. Check your connection and try again.'
        : 'Failed to load jobs. USAJobs.gov may be temporarily unavailable.');
      setJobs([]);
      setTotalJobs(0);
      setLoading(false);
      return;
    }
    const items  = data?.SearchResult?.SearchResultItems || [];
    const mapped = items.map(item => mapJob(item, matchReason)).filter(Boolean);
    setTotalJobs(data?.SearchResult?.SearchResultCountAll || 0);
    setJobs(mapped);
    setLoading(false);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    // Phase E: user-driven search honors locationInput + radius.
    // No 2210 routing on user re-search — user keyword wins.
    fetchJobs(searchInput, locationInput, remoteFilter, sortBy, undefined, radius);
  };

  const handleFilterChange = (type, value) => {
    setFilterDrawerOpen(false);
    if (type === 'remote') { setRemoteFilter(value); fetchJobs(search, locationInput, value,        sortBy, undefined, radius); }
    if (type === 'sort')   { setSortBy(value);       fetchJobs(search, locationInput, remoteFilter, value,  undefined, radius); }
    if (type === 'clear')  { setClearFilter(value); }
  };

  // ── Toggle save + persist saved_jobs + increment job_matches ─────
  // D-saved-jobs: saved_jobs persists to Supabase on every toggle so the
  // saved list survives refresh and cross-device sessions. job_matches is
  // still only incremented on save (not unsave) to preserve existing
  // dashboard counter semantics.
  const toggleSave = async (id) => {
    const alreadySaved = savedJobs.includes(id);
    const newSaved = alreadySaved
      ? savedJobs.filter(j => j !== id)
      : [...savedJobs, id];
    setSavedJobs(newSaved);

    if (!userId) return;
    if (!alreadySaved) {
      // Saving: persist new array AND increment job_matches in one update.
      const newCount = jobMatches + 1;
      setJobMatches(newCount);
      await supabase
        .from('profiles')
        .update({ saved_jobs: newSaved, job_matches: newCount })
        .eq('id', userId);
    } else {
      // Unsaving: persist new array only. job_matches is intentionally not
      // decremented — it's a lifetime count of saves, not a current total.
      await supabase
        .from('profiles')
        .update({ saved_jobs: newSaved })
        .eq('id', userId);
    }
  };

  // D-saved-jobs Phase 2: in Saved Only view, base array is the union of
  // (jobs in current fetch that are saved) + (cached saved jobs not in current
  // fetch). De-duplicate by id in case a saved job appears in both. Outside
  // Saved Only view, base is just the current jobs array.
  const baseJobs = showSavedOnly
    ? (() => {
        const seen = new Set();
        const out = [];
        jobs.forEach(j => {
          if (savedJobs.includes(j.id) && !seen.has(j.id)) {
            seen.add(j.id);
            out.push(j);
          }
        });
        savedJobs.forEach(id => {
          if (!seen.has(id) && savedJobsCache[id]) {
            seen.add(id);
            out.push(savedJobsCache[id]);
          }
        });
        return out;
      })()
    : jobs;

  const filtered = baseJobs.filter(job => {
    if (clearFilter === 'All Clearances') return true;
    return job.clearance === clearFilter;
  });

  // Phase 2: platform postings aren't part of the USAJobs fetch/filter cycle,
  // so the same clearance/remote/keyword filters are applied client-side here.
  const platformFiltered = showSavedOnly ? [] : platformJobs.filter(job => {
    if (clearFilter !== 'All Clearances' && job.clearance !== clearFilter) return false;
    if (remoteFilter !== 'All Locations' && job.remote !== remoteFilter) return false;
    if (search && !`${job.title} ${job.description}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const displayJobs = [...platformFiltered, ...filtered];

  const openBrief  = job => { setMessagingStatus('idle'); setApplyStatus('idle'); setApplyDraft(''); setApplyJob(job); };
  const closeBrief = ()  => { if (!applyPending.current) setApplyJob(null); };

  // Phase 3: submit a real application for a platform posting. Resume is a
  // snapshot taken now, independent of the veteran's general profile
  // visibility setting -- applying to a specific job is an explicit,
  // per-employer act of sharing, not the same as being browsable.
  const handleApply = async (job) => {
    if (!userId) { navigate('/auth'); return; }
    if (applyPending.current) return;
    applyPending.current = true;
    setApplyStatus('submitting');
    try {
    const { data, error } = await supabase
      .from('applications')
      .insert({
        job_posting_id: job.postingId,
        applicant_id: userId,
        cover_note: applyDraft.trim() || null,
        resume_url: resumeUrl || null,
        resume_name: resumeName || null,
      })
      .select('id, job_posting_id, status, cover_note, created_at, job_postings(title, employer_profiles:employer_profiles_public(company_name))')
      .single();

    if (error || !data) {
      setApplyStatus('error');
      return;
    }
    setMyApplications(prev => [data, ...prev]);
    setApplyStatus('idle');
    setApplyJob(null);

    // Best-effort: let the employer know. Not blocking on this -- a failed
    // notification insert shouldn't undo an already-submitted application.
    try { await supabase.from('notifications').insert({
      user_id: job.employerId,
      actor_id: userId,
      type: 'new_application',
      entity_id: job.postingId,
    }); } catch { /* The application already saved; do not offer a duplicate retry. */ }
    } catch {
      setApplyStatus('error');
    } finally {
      applyPending.current = false;
    }
  };

  const handleWithdraw = async (applicationId) => {
    if (withdrawalPending.current.has(applicationId)) return;
    withdrawalPending.current.add(applicationId);
    setWithdrawals(prev => ({ ...prev, [applicationId]: 'pending' }));
    try {
      const { data, error } = await supabase.rpc('withdraw_application', { p_application_id: applicationId });
      if (error || data?.ok !== true) throw new Error('Withdrawal not confirmed');
      setMyApplications(prev => prev.map(a => a.id === applicationId ? { ...a, status: 'withdrawn' } : a));
      setWithdrawals(prev => ({ ...prev, [applicationId]: 'done' }));
    } catch {
      setWithdrawals(prev => ({ ...prev, [applicationId]: 'error' }));
    } finally {
      withdrawalPending.current.delete(applicationId);
    }
  };

  // Message-the-employer remains available as a secondary action (e.g. a
  // pre-application question) alongside the real Apply flow below.
  const handleMessageEmployer = async (job) => {
    if (!userId) { navigate('/auth'); return; }
    setMessagingStatus('sending');
    const { data: convId, error } = await supabase.rpc('get_or_create_conversation', { other_user_id: job.employerId });
    if (error || !convId) { setMessagingStatus('error'); return; }
    closeBrief();
    // MessagesPage can't resolve a brand-new, message-less conversation from
    // the list alone (see MessagesPage.jsx mount effect) -- hand it the
    // identity we already know so it can open the thread directly.
    navigate('/messages', {
      state: {
        openConversation: {
          id: convId,
          otherId: job.employerId,
          name: job.company,
          initials: job.logo,
        },
      },
    });
  };

  return (
    <div className="jobs-root vq-surface">
      <AppNav />

      {/* ── HERO ── */}
      <div className="jobs-hero">
        <div className="jobs-hero-inner">
          <svg className="jobs-service-mark" aria-hidden="true" viewBox="0 0 48 56" fill="none"><path d="M5 5h38v29L24 51 5 34V5Z"/><path d="m14 21 10-7 10 7M14 31l10-7 10 7"/></svg>
          <p className="jobs-hero-eyebrow">Your next chapter after service</p>
          <h1 className="jobs-hero-title">Put your experience to work.</h1>
          <p className="jobs-hero-sub">Explore federal openings and roles posted on VetQualified. Review the requirements, consider the fit, and choose your next step.</p>
          <div className="jobs-search-bar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" aria-label="Search by job title, skill, or keyword" placeholder="Job title, skill, or keyword..." value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} />
            {searchInput && <button className="jobs-search-clear" aria-label="Clear search" onClick={() => setSearchInput('')}>✕</button>}
          </div>
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className="jobs-shell">
        {filterDrawerOpen && <div className="jobs-filter-backdrop" onClick={closeFilters} />}
        <aside className={`jobs-filters ${filterDrawerOpen ? 'drawer-open' : ''}`}>
          <div className="jobs-filter-drawer-header">
            <span>Filters</span>
            <button className="jobs-filter-drawer-close" onClick={closeFilters} aria-label="Close filters">✕</button>
          </div>
          {/* Phase E: mobile location + radius section */}
          <div className="jobs-filter-section">
            <div className="jobs-filter-label">Location</div>
            <div className="jobs-drawer-loc-wrap" ref={drawerLocWrapRef}>
              <input
                type="text"
                aria-label="Job location"
                placeholder="City, State — e.g., Las Vegas, NV"
                value={locationInput}
                onChange={e => handleLocChange(e.target.value)}
                onFocus={() => locationInput.trim().length >= 2 && setLocDropdownOpen(true)}
                onKeyDown={handleLocKeyDown}
                className="jobs-drawer-input"
                autoComplete="off"
              />
              {locDropdownOpen && locationInput.trim().length >= 2 && (
                <div className="jobs-loc-dropdown jobs-loc-dropdown-drawer">
                  {locLoading && locSuggestions.length === 0 && (
                    <div className="jobs-loc-empty">Searching…</div>
                  )}
                  {!locLoading && locSuggestions.length === 0 && (
                    <div className="jobs-loc-empty">No location suggestions found.</div>
                  )}
                  {locSuggestions.map((s, i) => (
                    <button
                      key={s.placeId}
                      type="button"
                      className={`jobs-loc-suggestion ${i === locHighlight ? 'highlight' : ''}`}
                      onMouseDown={e => { e.preventDefault(); handleLocSelect(s); }}
                      onMouseEnter={() => setLocHighlight(i)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="jobs-filter-label" style={{ marginTop: '0.85rem' }}>Radius</div>
            {RADIUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`jobs-filter-btn ${radius === opt.value ? 'active' : ''}`}
                onClick={() => { setRadius(opt.value); }}
              >
                {opt.label}
              </button>
            ))}
            <button
              className="jobs-drawer-apply"
              onClick={() => { closeFilters(); handleSearch(); }}
            >
              Apply Location
            </button>
          </div>
          <div className="jobs-filter-section">
            <div className="jobs-filter-label">Clearance Required</div>
            {CLEARANCE_LEVELS.map(level => (
              <button key={level} className={`jobs-filter-btn ${clearFilter === level ? 'active' : ''}`} onClick={() => handleFilterChange('clear', level)}>{level}</button>
            ))}
          </div>
          <div className="jobs-filter-section">
            <div className="jobs-filter-label">Work Location</div>
            {REMOTE_OPTIONS.map(opt => (
              <button key={opt} className={`jobs-filter-btn ${remoteFilter === opt ? 'active' : ''}`} onClick={() => handleFilterChange('remote', opt)}>{opt}</button>
            ))}
          </div>
          <div className="jobs-filter-section">
            <div className="jobs-filter-label">Sort By</div>
            {SORT_OPTIONS.map(opt => (
              <button key={opt} className={`jobs-filter-btn ${sortBy === opt ? 'active' : ''}`} onClick={() => handleFilterChange('sort', opt)}>{opt}</button>
            ))}
          </div>
          <div className="jobs-filter-tip">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <p>Showing jobs based on your profile career goal. Type a search above to explore other roles.</p>
          </div>
          {savedJobs.length > 0 && (
            <div className="jobs-filter-tip" style={{ marginTop: '0.5rem' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
              <p><strong style={{ color: '#C9A84C' }}>{savedJobs.length}</strong> job{savedJobs.length !== 1 ? 's' : ''} saved · {jobMatches} total matches tracked</p>
            </div>
          )}
        </aside>

        <main className="jobs-main">
          <div className="jobs-action-row">
            <button
              className="jobs-filter-trigger"
              ref={filterTriggerRef}
              aria-expanded={filterDrawerOpen}
              onClick={() => setFilterDrawerOpen(true)}
              aria-label="Open filters"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>
              Filters
              {(clearFilter !== 'All Clearances' || remoteFilter !== 'All Locations' || sortBy !== 'Newest') && (
                <span className="jobs-filter-trigger-badge">
                  {[clearFilter !== 'All Clearances', remoteFilter !== 'All Locations', sortBy !== 'Newest'].filter(Boolean).length}
                </span>
              )}
            </button>
            <button
              className={`jobs-saved-toggle ${showSavedOnly ? 'active' : ''}`}
              onClick={() => setShowSavedOnly(v => !v)}
              aria-label="Toggle saved jobs"
            >
              <svg className="jobs-saved-toggle-icon" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
              {showSavedOnly ? 'All Jobs' : 'Saved Jobs'}
              {savedJobs.length > 0 && (
                <span className="jobs-saved-toggle-badge">{savedJobs.length}</span>
              )}
            </button>
            {userId && (
              <button
                className={`jobs-saved-toggle ${showApplicationsOnly ? 'active' : ''}`}
                onClick={() => setShowApplicationsOnly(v => !v)}
                aria-label="Toggle my applications"
              >
                <svg className="jobs-saved-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                {showApplicationsOnly ? 'All Jobs' : 'My Applications'}
                {myApplications.length > 0 && (
                  <span className="jobs-saved-toggle-badge">{myApplications.length}</span>
                )}
              </button>
            )}
          </div>
          {showApplicationsOnly ? (
            <>
              <div className="jobs-results-header">
                <span className="jobs-count"><strong>{myApplications.length}</strong> application{myApplications.length !== 1 ? 's' : ''}</span>
              </div>
              {myApplications.length === 0 && (
                <div className="jobs-empty vq-card">
                  <h2>Your applications will appear here</h2>
                  <p>You haven't applied to any VetQualified-posted roles yet. Look for the "Posted on VetQualified" badge on job listings.</p>
                </div>
              )}
              <div className="jobs-list">
                {myApplications.map(app => (
                  <div key={app.id} className="jobs-card vq-card">
                    <div className="jobs-card-top">
                      <div className="jobs-logo" style={{ background: '#C9A84C22', color: '#C9A84C', border: '1px solid #C9A84C44' }}>
                        {(app.job_postings?.employer_profiles?.company_name || 'VQ').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="jobs-card-main">
                        <div className="jobs-card-header">
                          <div>
                            <h3 className="jobs-title">{app.job_postings?.title || 'Posting no longer available'}</h3>
                            <div className="jobs-company">{app.job_postings?.employer_profiles?.company_name || 'VetQualified Employer'}</div>
                          </div>
                          <span className={`jobs-status-pill ${app.status}`}>{APP_STATUS_LABEL[app.status]}</span>
                        </div>
                        <div className="jobs-facts">
                          <span className="jobs-posted">Applied {timeAgo(app.created_at)}</span>
                        </div>
                        {app.cover_note && <div className="jobs-description open"><p>{app.cover_note}</p></div>}
                      </div>
                    </div>
                    {withdrawals[app.id] === 'error' && <p role="alert" className="empd-error">Could not confirm withdrawal. Your last confirmed status is shown. Please try again.</p>}
                    {WITHDRAWABLE_STATUSES.includes(app.status) && (
                      <div className="jobs-card-footer">
                        <div />
                        <div className="jobs-footer-actions">
                          <button className="jobs-apply-btn" disabled={withdrawals[app.id] === 'pending'} onClick={() => handleWithdraw(app.id)}>{withdrawals[app.id] === 'pending' ? 'Withdrawing…' : 'Withdraw'}</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
          <>
          <div className="jobs-results-header">
            {!loading && !error && (
              <span className="jobs-count">
                <strong>{totalJobs.toLocaleString()}</strong> federal positions
                {platformFiltered.length > 0 && <> · <strong>{platformFiltered.length}</strong> posted on VetQualified</>}
                {search && <span> for <strong style={{color:'#C9A84C'}}>{search}</strong></span>}
              </span>
            )}
            {savedJobs.length > 0 && <span className="jobs-saved-count">{savedJobs.length} saved</span>}
            {loadingSavedJobs && <span className="jobs-saved-loading">Loading saved jobs…</span>}
          </div>

          {loading && (
            <div className="jobs-empty vq-card" role="status">
              <div className="jobs-spinner" aria-hidden="true"/>
              <h2>Finding your next opportunity</h2>
              <p>Searching federal listings. Job titles, requirements, and application links will appear here.</p>
            </div>
          )}

          {error && <div className="jobs-empty vq-card" role="alert"><h2>Job listings couldn’t load</h2><p>{error}</p><p>Try your search again in a moment.</p></div>}

          {!loading && !error && !loadingSavedJobs && filtered.length === 0 && (
            <div className="jobs-empty vq-card">
              <h2>{showSavedOnly ? 'Your saved opportunities' : 'No matching opportunities'}</h2>
              <p>{showSavedOnly
                ? (savedJobs.length === 0
                    ? 'You haven\'t saved any jobs yet. Tap the bookmark icon on a job card to save it for later.'
                    : 'No saved jobs match your current filters. Try clearing filters or switching back to All Jobs.')
                : 'No jobs match your current filters. Try a different keyword or clear the clearance filter.'}</p>
              <button onClick={() => setClearFilter('All Clearances')}>Clear filters</button>
            </div>
          )}

          <div className="jobs-list">
            {!loading && !error && displayJobs.map(job => job.unavailable ? (
              <div key={job.id} className="jobs-card vq-card jobs-card-unavailable">
                <div className="jobs-card-top">
                  <div className="jobs-logo jobs-logo-unavailable">—</div>
                  <div className="jobs-card-main">
                    <div className="jobs-card-header">
                      <div>
                        <h3 className="jobs-title">Saved job no longer available</h3>
                        <div className="jobs-company">USAJobs ID: {job.id}</div>
                      </div>
                      <div className="jobs-card-actions">
                        <button
                          className="jobs-save-btn saved"
                          onClick={() => toggleSave(job.id)}
                          title="Remove from saved"
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <p className="jobs-unavailable-note">
                      This federal listing has expired or been removed from USAJobs.gov.
                      Tap the bookmark to remove it from your saved list.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div key={job.id} className={`jobs-card vq-card ${expandedJob === job.id ? 'expanded' : ''}`}>
                <div className="jobs-card-top">
                  <div className="jobs-logo" style={{ background: job.logoColor + '22', color: job.logoColor, border: `1px solid ${job.logoColor}44` }}>{job.logo}</div>
                  <div className="jobs-card-main">
                    <div className="jobs-card-header">
                      <div>
                        <h3 className="jobs-title">{job.title}</h3>
                        <div className="jobs-company">
                          {job.company}
                          {job.source === 'platform' && <span className="jobs-vet-badge">Posted on VetQualified</span>}
                          {job.source === 'usajobs' && <span className="jobs-source-badge">Source: USAJOBS</span>}
                        </div>
                      </div>
                      <div className="jobs-card-actions">
                        {job.source !== 'platform' && (
                          <button
                            className={`jobs-save-btn ${savedJobs.includes(job.id) ? 'saved' : ''}`}
                            onClick={() => toggleSave(job.id)}
                            title={savedJobs.includes(job.id) ? 'Unsave' : 'Save job'}
                          >
                            <svg viewBox="0 0 24 24" fill={savedJobs.includes(job.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="jobs-facts">
                      <span
                        className={`jobs-clearance-pill ${job.clearanceCode}`}
                        title={job.source === 'usajobs'
                          ? (job.clearanceInferred
                              ? 'Detected by VetQualified from the listing text — not an official USAJOBS field. Verify on USAJOBS.'
                              : 'The listing text does not state a clearance requirement.')
                          : undefined}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                        {job.clearance}
                      </span>
                      <span className="jobs-fact-pill">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {job.location}
                      </span>
                      <span className={`jobs-remote-pill ${job.remote.toLowerCase().replace('-','')}`}>{job.remote}</span>
                      <span className="jobs-fact-pill">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                        {job.salary}
                      </span>
                      <span className="jobs-posted">{job.posted}</span>
                    </div>

                    {job.matchReasons.length > 0 && (
                      <div className="jobs-match-row">
                        <span className="jobs-match-label">Match</span>
                        <span className="jobs-match-reason">{job.matchReasons[0]}</span>
                      </div>
                    )}

                    {job.tags.length > 0 && (
                      <div className="jobs-tags">
                        {job.tags.map(tag => <span key={tag} className="jobs-tag">{tag}</span>)}
                      </div>
                    )}
                  </div>
                </div>

                {job.source === 'platform' ? <>
                  <p className="jobs-preview-summary">{job.summary || job.description?.slice(0, 350) || 'Open this posting for details.'}</p>
                  {expandedJob === job.id && <PostingBody posting={job.posting}/>}
                </> : <div className={`jobs-description ${expandedJob === job.id ? 'open' : ''}`}>
                  <p>{job.description || 'See full job description on USAJobs.gov.'}</p>
                </div>}

                <div className="jobs-card-footer">
                  <button className="jobs-expand-btn" aria-expanded={expandedJob === job.id} onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                    {expandedJob === job.id ? 'Show less ↑' : 'View details ↓'}
                  </button>
                  <div className="jobs-footer-actions">
                    {job.source === 'usajobs' && (
                      <a
                        className="jobs-view-source-link"
                        href={job.positionUri}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on USAJOBS
                      </a>
                    )}
                    <button className="jobs-apply-btn" onClick={() => openBrief(job)}>
                      {job.source === 'platform'
                        ? (applicationsByPostingId[job.postingId]
                            ? `Applied · ${APP_STATUS_LABEL[applicationsByPostingId[job.postingId].status]}`
                            : 'Apply')
                        : 'Apply on USAJobs'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
          )}
        </main>
      </div>

      <footer className="jobs-footer">
        <div className="jobs-footer-logo">Vet<span>Qualified</span></div>
        <div>Job data provided by USAJobs.gov · Built by veterans. For every veteran who comes after.</div>
      </footer>

      {/* ── PRE-MISSION BRIEF MODAL (USAJobs) / APPLY MODAL (platform) ── */}
      {applyJob && applyJob.source === 'platform' && (() => {
        const existingApp = applicationsByPostingId[applyJob.postingId];
        return (
        <div className="jobs-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeBrief(); }}>
          <div className="jobs-modal">
            <div className="jobs-modal-header">
              <div>
                <h2 className="jobs-modal-title">{existingApp ? 'Application Status' : `Apply to ${applyJob.company}`}</h2>
                <p className="jobs-modal-company">{applyJob.title} · {applyJob.company}</p>
              </div>
              <button className="jobs-modal-close" onClick={closeBrief}>✕</button>
            </div>

            {existingApp ? (
              <>
                <div className="jobs-brief-banner">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <p>You applied {timeAgo(existingApp.created_at)}. Current status: <strong>{APP_STATUS_LABEL[existingApp.status]}</strong>.</p>
                </div>
                {withdrawals[existingApp.id] === 'error' && <p role="alert" className="empd-error">Could not confirm withdrawal. Your last confirmed status is shown. Please try again.</p>}
                <div className="jobs-modal-footer">
                  <button className="jobs-modal-cancel" onClick={closeBrief}>Close</button>
                  {WITHDRAWABLE_STATUSES.includes(existingApp.status) && (
                    <button className="jobs-modal-submit" disabled={withdrawals[existingApp.id] === 'pending'} onClick={() => handleWithdraw(existingApp.id)}>
                      {withdrawals[existingApp.id] === 'pending' ? 'Withdrawing…' : 'Withdraw Application'}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="jobs-brief-banner">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <p>{resumeUrl
                    ? 'Submitting shares your profile, application note, and résumé with this employer, including information otherwise kept out of candidate discovery.'
                    : "You haven't uploaded a resume yet — submitting still shares your profile and application note with this employer. Add a résumé from your Dashboard first if you want to include it."}</p>
                </div>
                <p><a href="/privacy" target="_blank" rel="noopener noreferrer">Review privacy and application sharing</a></p>
                <textarea
                  className="jobs-apply-note"
                  rows={4}
                  placeholder="Add a short note (optional) — why you're a fit for this role."
                  value={applyDraft}
                  onChange={e => setApplyDraft(e.target.value)}
                />
                {applyStatus === 'error' && <div role="alert" className="empd-error" style={{ margin: '0.85rem 0 0' }}>Could not submit your application. Please try again.</div>}
                <div className="jobs-modal-footer">
                  <button className="jobs-modal-cancel" onClick={closeBrief}>Not Now</button>
                  <button
                    className="jobs-modal-submit"
                    onClick={() => handleApply(applyJob)}
                    disabled={applyStatus === 'submitting'}
                  >
                    {applyStatus === 'submitting' ? 'Submitting…' : userId ? 'Submit Application' : 'Sign In to Apply'}
                  </button>
                </div>
                <div className="jobs-modal-secondary">
                  <button className="empd-link-btn" onClick={() => handleMessageEmployer(applyJob)}>
                    {messagingStatus === 'sending' ? 'Starting…' : 'Or message the employer with a question →'}
                  </button>
                  {messagingStatus === 'error' && <div className="empd-error" style={{ marginTop: '0.6rem' }}>Could not start a conversation. Please try again.</div>}
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}
      {applyJob && applyJob.source !== 'platform' && (
        <div className="jobs-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeBrief(); }}>
          <div className="jobs-modal">
            <div className="jobs-modal-header">
              <div>
                <h2 className="jobs-modal-title">Pre-Mission Brief</h2>
                <p className="jobs-modal-company">{applyJob.title} · {applyJob.company}</p>
              </div>
              <button className="jobs-modal-close" onClick={closeBrief}>✕</button>
            </div>
            <div className="jobs-brief-banner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              <p>Clicking <strong>I'm Ready</strong> will take you to USAJobs.gov to complete your application. Make sure your profile and resume are ready before you proceed.</p>
            </div>
            <div className="jobs-brief-heading">
              <span className="jobs-brief-label">Readiness Check</span>
              <p className="jobs-brief-sub">Before you execute — confirm your kit is squared away.</p>
            </div>
            <div className="jobs-checklist">
              {[
                { icon: '📄', title: 'Resume is current and legible',        body: "Gaps, typos, and military jargon without translation are disqualifiers. If a civilian hiring manager can't read it in 30 seconds, it's not ready." },
                { icon: '🎯', title: 'Your experience matches this role',     body: "Don't spray and pray. A targeted application always outperforms a generic one." },
                { icon: '🔐', title: 'Clearance status is accurately listed', body: "Misrepresenting your clearance in either direction will end the conversation immediately." },
                { icon: '✍️', title: 'Your bio translates your service',      body: "You led people, managed risk, and executed under pressure. Make sure your profile reflects that in civilian language." },
              ].map((item, i) => (
                <div key={i} className="jobs-checklist-item">
                  <span className="jobs-checklist-icon">{item.icon}</span>
                  <div>
                    <div className="jobs-checklist-title">{item.title}</div>
                    <div className="jobs-checklist-body">{item.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="jobs-brief-footer-note">You served with standards. Apply with the same ones.</div>
            <div className="jobs-modal-footer">
              <button className="jobs-modal-cancel" onClick={closeBrief}>Not Ready — Go Back</button>
              <a
                href={applyJob.applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="jobs-modal-submit"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                onClick={closeBrief}
              >
                I'm Ready — Apply on USAJobs
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
